/**
 * UsersContext stores user state on the client side. For those familiar with
 * the redux world, it's like the Users store and includes both Users actions
 * and the Users reducer.
 */

import React, { createContext, useEffect, useReducer } from "react";

import { userOrUndefined, getUser } from "src/context/UsersContext/selectors";
import {
  UsersAction,
  UsersActionType,
  UserIdentifier,
  UsersState,
  USER_NOT_FOUND,
  FETCHING_USER,
  UserInput,
  ContextUser,
} from "src/context/UsersContext/types";

function updateStateWithUser(
  state: UsersState,
  newUserData: UserInput
): UsersState {
  const existingUser = state.usersById.get(newUserData.id);
  // Merge incoming data with existing data. When receiving light feed responses,
  // existing full data is preserved. Cast is safe due to merging logic.
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const updatedUser: ContextUser = {
    ...(typeof existingUser === "object" && existingUser),
    ...newUserData,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as any;
  return {
    usersById: new Map(state.usersById).set(updatedUser.id, updatedUser),
    usersByUsername: updatedUser.username
      ? new Map(state.usersByUsername).set(updatedUser.username, updatedUser)
      : state.usersByUsername,
  };
}

function updateStateWithStatus(
  state: UsersState,
  identifier: UserIdentifier,
  status: typeof USER_NOT_FOUND | typeof FETCHING_USER
): UsersState {
  const { usersById, usersByUsername } = state;
  const user = userOrUndefined(getUser(state, identifier));
  if (user) {
    return state;
  }
  if ("username" in identifier) {
    if (usersByUsername.get(identifier.username) !== status) {
      return {
        usersById: new Map(usersById),
        usersByUsername: new Map(usersByUsername).set(
          identifier.username,
          status
        ),
      };
    }
    return state;
  }
  return {
    usersById: new Map(usersById).set(identifier.id, status),
    usersByUsername: new Map(usersByUsername),
  };
}

function updateStateWithManyUsers(state: UsersState, users: UserInput[]) {
  return users.reduce(
    (curState, curUser) => updateStateWithUser(curState, curUser),
    state
  );
}

function usersReducer(state: UsersState, action: UsersAction): UsersState {
  switch (action.type) {
    case UsersActionType.FETCHING_USER:
      return updateStateWithStatus(state, action.data, FETCHING_USER);
    case UsersActionType.USER_NOT_FOUND:
      return updateStateWithStatus(state, action.data, USER_NOT_FOUND);
    case UsersActionType.ADD_USER:
      return updateStateWithUser(state, action.data);
    case UsersActionType.ADD_USERS:
      return updateStateWithManyUsers(state, action.data);
    default:
      throw new Error(`User action with unknown type: ${action}`);
  }
}

const INITIAL_USERS_STATE: UsersState = {
  usersById: new Map(),
  usersByUsername: new Map(),
};

export let dispatchUsersAction: React.Dispatch<UsersAction>;

export const UsersContext = createContext<UsersState>(INITIAL_USERS_STATE);

export const UsersProvider: React.FCC<{ initialData?: UserInput[] }> = ({
  children,
  initialData,
}) => {
  const [usersState, usersDispatcher] = useReducer(
    usersReducer,
    initialData
      ? updateStateWithManyUsers(INITIAL_USERS_STATE, initialData)
      : INITIAL_USERS_STATE
  );

  dispatchUsersAction = usersDispatcher;

  useEffect(() => {
    usersDispatcher({
      type: UsersActionType.ADD_USERS,
      data: initialData || [],
    });
  }, [initialData]);

  return (
    <UsersContext.Provider value={usersState}>{children}</UsersContext.Provider>
  );
};
